import { metricKeys, SESSION_JOURNEY_VERSION, type AccountingFrame, type GameEvent, type GameStateSnapshot, type LiveRateSnapshot, type SessionAggregate, type SessionIngestResult, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { SessionExpCounter } from './session-exp-counter'
import { SessionJourneyRecorder } from './session-journey'
import { TrustedMetricLedger } from './trusted-metric-ledger'

const emptyCoverage = () => Object.fromEntries(metricKeys.map((key) => [key, 0])) as SessionAggregate['coverage']
export const SESSION_ACCOUNTING_VERSION = 6
const HOUR_MS = 3_600_000

export class SessionEngine {
  private record: SessionRecord | null = null
  private pauseStartedAt: number | null = null
  private accumulatedPauseMs = 0
  private latestState: GameStateSnapshot | null = null
  private captureGeneration = 0
  private readonly ledger: TrustedMetricLedger
  private readonly expCounter: SessionExpCounter
  private readonly journeyRecorder = new SessionJourneyRecorder()
  private latestAccounting: AccountingFrame | null = null

  constructor(private readonly preset: GamePreset) {
    this.ledger = new TrustedMetricLedger(preset)
    this.expCounter = new SessionExpCounter()
  }

  start(
    profileId: string,
    now = Date.now(),
    initialState: GameStateSnapshot | null = null,
    captureGeneration = 0,
  ): SessionRecord {
    if (this.record && this.record.status !== 'ended') throw new Error('A record is already active')
    this.pauseStartedAt = null
    this.accumulatedPauseMs = 0
    this.latestState = initialState
    this.latestAccounting = null
    this.captureGeneration = captureGeneration
    this.ledger.reset(now, captureGeneration, initialState)
    this.expCounter.reset(initialState)
    this.record = {
      id: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      profileId,
      status: 'running',
      startedAt: now,
      activeDurationMs: 0,
      sessionAccountingVersion: SESSION_ACCOUNTING_VERSION,
      journeyVersion: SESSION_JOURNEY_VERSION,
      aggregate: this.emptyAggregate(),
      ...(initialState ? { startState: initialState, endState: initialState } : {}),
    }
    const record = this.update({}, now)
    this.journeyRecorder.start(record, initialState)
    return record
  }

  pause(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    const record = this.update({ status: 'paused', pausedAt: now }, now)
    this.journeyRecorder.mark('pause', record, now)
    return record
  }

  resume(now = Date.now(), captureGeneration = this.captureGeneration + 1): SessionRecord {
    this.assertStatus('paused', 'interrupted')
    const previousStatus = this.record!.status
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.captureGeneration = captureGeneration
    this.ledger.resume(now, captureGeneration)
    const record = this.update({ status: 'running', pausedAt: undefined }, now)
    this.journeyRecorder.mark(previousStatus === 'interrupted' ? 'capture-restored' : 'resume', record, now)
    return record
  }

  interrupt(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    const record = this.update({ status: 'interrupted', pausedAt: now }, now)
    this.journeyRecorder.mark('capture-interrupted', record, now)
    return record
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionRecord {
    return this.ingestWithAccounting(state, events, observation, captureGeneration).record
  }

  ingestWithAccounting(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionIngestResult {
    this.assertStatus('running')
    this.captureGeneration = captureGeneration
    const activeAt = this.activeDuration(state.capturedAt)
    const accounting = this.ledger.ingest(state, events, observation, activeAt, captureGeneration)
    this.expCounter.ingestCandidate(observation?.expExactCandidate)
    this.expCounter.ingest(accounting.exp)
    this.latestAccounting = accounting
    const startState = this.record!.startState ?? state
    this.latestState = state
    const record = this.update({ startState, endState: state }, state.capturedAt)
    this.journeyRecorder.ingest(record, accounting, events, state)
    return { record, accounting }
  }

  end(now = Date.now()): SessionRecord {
    this.assertStatus('running', 'paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    const record = this.update({ status: 'ended', endedAt: now, pausedAt: undefined }, now)
    this.journeyRecorder.finish(record, now)
    return record
  }

  current(now = Date.now()): SessionRecord | null {
    if (!this.record) return null
    return this.update({}, now)
  }

  liveRates(now = this.latestState?.capturedAt ?? Date.now()): LiveRateSnapshot {
    const trusted = this.ledger.liveRates(now)
    const durationMs = this.currentActiveDuration(now)
    const gained = this.expCounter.total()
    return {
      ...trusted,
      expPerHour: {
        value: this.expPerHour(gained, durationMs),
        status: gained === null ? 'unavailable' : 'ready',
        activeDurationMs: durationMs,
        updatedAt: this.expCounter.latestSourceAt(),
      },
    }
  }

  accountingFrame(): AccountingFrame | null {
    return this.latestAccounting
  }

  expDisplayStatus() {
    return this.expCounter.endpointStatus()
  }

  journey(): SessionJourney | null {
    return this.journeyRecorder.snapshot()
  }

  private update(patch: Partial<SessionRecord>, now: number): SessionRecord {
    if (!this.record) throw new Error('No active record')
    const activeDurationMs = this.currentActiveDuration(now)
    const next = { ...this.record, ...patch, activeDurationMs }
    this.record = { ...next, aggregate: this.aggregate(next, activeDurationMs) }
    return this.record
  }

  private activeDuration(now: number): number {
    if (!this.record) return 0
    const effectiveNow = this.record.status === 'ended' && this.record.endedAt !== undefined
      ? Math.min(now, this.record.endedAt)
      : now
    const currentPause = this.pauseStartedAt === null ? 0 : Math.max(0, effectiveNow - this.pauseStartedAt)
    return Math.max(0, effectiveNow - this.record.startedAt - this.accumulatedPauseMs - currentPause)
  }

  private currentActiveDuration(now: number): number {
    return Math.max(this.record?.activeDurationMs ?? 0, this.activeDuration(now))
  }

  private aggregate(record: SessionRecord, durationMs: number): SessionAggregate {
    const end = this.latestState ?? record.endState
    const trustedAggregate = this.ledger.aggregate()
    const expGained = this.expCounter.total()
    return {
      durationMs,
      ...trustedAggregate,
      expGained,
      expGainedApproximate: this.expCounter.approximate(),
      expRateGained: trustedAggregate.expGained,
      expPerHour: this.expPerHour(expGained, durationMs),
      coverage: end?.coverage ?? emptyCoverage(),
    }
  }

  private emptyAggregate(): SessionAggregate {
    return {
      durationMs: 0,
      expGained: null,
      expRateGained: null,
      expTrustedDurationMs: 0,
      expTrustedPerHour: null,
      expPerHour: null,
      mesosStartBalance: null,
      mesosCurrentBalance: null,
      mesosBalanceChange: null,
      mesosDelta: null,
      mesosTrustedDurationMs: 0,
      mesosPerHour: null,
      hpConsumed: 0,
      hpConsumedPerHour: null,
      hpRecovered: 0,
      hpRecoveredPerHour: null,
      mpConsumed: 0,
      mpConsumedPerHour: null,
      mpRecovered: 0,
      mpRecoveredPerHour: null,
      coverage: emptyCoverage(),
    }
  }

  private expPerHour(gained: number | null, durationMs: number): number | null {
    if (gained === null) return null
    const value = durationMs > 0 ? gained / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : null
  }

  private assertStatus(...allowed: SessionRecord['status'][]): void {
    if (!this.record || !allowed.includes(this.record.status)) throw new Error(`Invalid record transition from ${this.record?.status ?? 'none'}`)
  }
}
